import { GetOpsLocationsForMonitoring } from '../libs/GraphQL';

// Per-day localStorage cache for the Monitoring tab's OpsLocations
// projection (coordinates + prettyName for map pins). Separate cache key
// from the ViewStockingDashboard `opsLocations` cache because the
// projections differ.
//
// v2 invalidated same-day caches written before the projection stabilized.
// The query fetches only the nested `coordinates` object — the flat
// latitude/longitude scalars are not part of the filtered opsLocations
// query (see GetOpsLocationsForMonitoring in libs/GraphQL.js); the flat
// fallback lives in composeDriverRecords for locations that only populate
// the scalars.
const OPS_LOCATIONS_CACHE_KEY = 'opsLocationsForMonitoring:v2';

function todayDateString() {
  return new Date().toISOString().slice(0, 10);
}

function readOpsLocationsFromCache() {
  try {
    const raw = localStorage.getItem(OPS_LOCATIONS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.date !== todayDateString()) return null;
    return Array.isArray(parsed?.locations) ? parsed.locations : null;
  } catch {
    return null;
  }
}

function writeOpsLocationsToCache(locations) {
  try {
    localStorage.setItem(
      OPS_LOCATIONS_CACHE_KEY,
      JSON.stringify({ date: todayDateString(), locations }),
    );
  } catch {
    // Quota exceeded or storage unavailable — non-fatal.
  }
}

async function fetchAllOpsLocations() {
  const all = [];
  let nextToken = null;
  do {
    // Sequential pagination: each page's request needs the previous page's
    // nextToken, so the awaits can't be parallelized.
    // eslint-disable-next-line no-await-in-loop
    const page = await GetOpsLocationsForMonitoring(nextToken);
    if (Array.isArray(page?.locations)) all.push(...page.locations);
    nextToken = page?.nextToken || null;
  } while (nextToken);
  return all;
}

// `force` skips the per-day cache so an explicit Apply Filters always hits
// GetOpsLocationsForMonitoring; the initial mount stays cache-first.
export default async function loadOpsLocations(force = false) {
  if (!force) {
    const cached = readOpsLocationsFromCache();
    if (cached && cached.length > 0) return cached;
  }
  const fresh = await fetchAllOpsLocations();
  if (fresh.length > 0) writeOpsLocationsToCache(fresh);
  return fresh;
}
